// Utilitaires de robustesse de mot de passe — politique NIST 2024 minimaliste.
// - Longueur minimale : 10 caractères (pas de règle de complexité/rotation).
// - Contrôle HIBP (Have I Been Pwned) via k-anonymity : on n'envoie JAMAIS le mot
//   de passe ni son hash complet, seulement les 5 premiers caractères du SHA-1.

export const PASSWORD_MIN_LENGTH = 10;

// SHA-1 hex (majuscules) via l'API Web Crypto du navigateur.
async function sha1HexUpper(text) {
  const data = new TextEncoder().encode(text);
  const buffer = await crypto.subtle.digest('SHA-1', data);
  return Array.from(new Uint8Array(buffer))
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('')
    .toUpperCase();
}

/**
 * Renvoie le nombre de fuites connues pour ce mot de passe (0 = jamais vu).
 * Fail-open : en cas d'erreur réseau, renvoie 0 (on ne bloque pas l'utilisateur).
 * Utilise le fetch BRUT (sans l'intercepteur d'auth global) pour éviter d'envoyer
 * le header Authorization à un domaine tiers.
 */
export async function checkPwnedCount(password) {
  if (!password) return 0;
  try {
    const hash = await sha1HexUpper(password);
    const prefix = hash.slice(0, 5);
    const suffix = hash.slice(5);
    const rawFetch = (typeof window !== 'undefined' && window.__rawFetch) || fetch;
    const res = await rawFetch(`https://api.pwnedpasswords.com/range/${prefix}`, {
      headers: { 'Add-Padding': 'true' },
    });
    if (!res.ok) return 0;
    const text = await res.text();
    for (const line of text.split('\n')) {
      const [suf, count] = line.trim().split(':');
      if (suf === suffix) return parseInt(count, 10) || 0;
    }
    return 0;
  } catch {
    return 0;
  }
}

/**
 * Génère un mot de passe fort aléatoire (cryptographiquement sûr) garantissant
 * au moins une minuscule, une majuscule, un chiffre et un symbole, longueur 16.
 * Utilisé par les écrans admin (bouton « Générer un mot de passe fort »).
 * Caractères ambigus (O/0, l/1, I) exclus pour la lisibilité lors d'une transmission.
 */
export function generateStrongPassword(length = 16) {
  const lower = 'abcdefghijkmnpqrstuvwxyz';
  const upper = 'ABCDEFGHJKLMNPQRSTUVWXYZ';
  const digits = '23456789';
  const symbols = '!@#$%&*?-_';
  const all = lower + upper + digits + symbols;
  const rnd = (max) => {
    const buf = new Uint32Array(1);
    crypto.getRandomValues(buf);
    return Math.floor((buf[0] / 2 ** 32) * max);
  };
  const pick = (set) => set[rnd(set.length)];
  const chars = [pick(lower), pick(upper), pick(digits), pick(symbols)];
  for (let i = chars.length; i < length; i++) chars.push(pick(all));
  // Mélange Fisher-Yates pour ne pas exposer la position des classes garanties.
  for (let i = chars.length - 1; i > 0; i--) {
    const j = rnd(i + 1);
    [chars[i], chars[j]] = [chars[j], chars[i]];
  }
  return chars.join('');
}

/**
 * Évalue la robustesse → { score (1-4), label, color, percent }.
 * Couleurs alignées sur le Design System (rouge / ambre / bleu / vert).
 */
export function evaluateStrength(password) {
  const pwd = password || '';
  if (pwd.length === 0) {
    return { score: 0, label: '', color: '#E5E7EB', percent: 0 };
  }
  if (pwd.length < PASSWORD_MIN_LENGTH) {
    return { score: 1, label: 'Trop court', color: '#DC2626', percent: 20 };
  }
  let score = 1;
  if (pwd.length >= 10) score += 1;
  if (pwd.length >= 14) score += 1;
  const variety = [/[a-z]/, /[A-Z]/, /[0-9]/, /[^A-Za-z0-9]/].filter((r) => r.test(pwd)).length;
  if (variety >= 3 && pwd.length >= 12) score += 1;
  score = Math.min(4, Math.max(1, score));
  const map = {
    1: { label: 'Faible', color: '#DC2626', percent: 25 },
    2: { label: 'Moyen', color: '#F59E0B', percent: 50 },
    3: { label: 'Fort', color: '#007BFF', percent: 75 },
    4: { label: 'Excellent', color: '#28A745', percent: 100 },
  };
  return { score, ...map[score] };
}
